import { Link } from 'react-router-dom';
import { FileText, ExternalLink, Play } from 'lucide-react';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import { BTN_PRIMARY } from '../components/styles';
import { COMPATIBILITY_LIMITS, INSTALL_RESOURCES } from '../content/products';
import OperationVideo from '../components/OperationVideo';
import usePageMeta from '../hooks/usePageMeta';

export default function HowItWorks() {
  usePageMeta(
    'How It Works | RangeSafe Commercial Solutions',
    'How RangeSafe stove safety knobs operate: press both sides, push toward the stove, turn to the desired setting. Classic and Ultimate mechanisms explained.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="The Mechanism"
          lines={['Push', 'Press', 'Turn']}
          lead="RangeSafe knobs replace a stove's factory knobs and use a deliberate three-part motion. A standard knob can be switched on with a light twist — by a hip, a mop handle or a curious hand. A RangeSafe knob does nothing until the sequence is performed on purpose."
        />
      </header>

      <section className="mx-auto max-w-shell px-5 sm:px-8" data-testid="how-steps">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <OperationVideo
              src="/assets/video/knob-macro-loop.mp4"
              srcWebm="/assets/video/knob-macro-loop.webm"
              poster="/assets/video/knob-macro-poster.jpg"
              caption="Both sides must be pressed before the knob can move"
              alt="Video of a RangeSafe stove safety knob in operation: the knob is pushed toward the stove with both sides pressed, turned, then it returns to rest. To operate a RangeSafe knob, push it in while pressing both sides, then turn."
              testid="how-mechanism-video"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <ol className="space-y-0 border-t border-line">
              {[
                ['Push', 'The knob only moves inward, toward the stove — never out toward you.'],
                ['Press', 'Both sides of the knob must be pressed together as it is pushed in.'],
                ['Turn', 'Turn to the desired setting. Return to off and the mechanism re-locks automatically.'],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-6 border-b border-line py-7">
                  <span className="font-mono text-[11px] tracking-[0.3em] text-accentText">0{i + 1}</span>
                  <div>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">{t}</h2>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-dim">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-panel/40" data-testid="how-demo">
        <div className="mx-auto grid max-w-shell gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:py-28">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">Demonstration</p>
            <h2 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
              Watch the installation
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-dim sm:text-base">
              The official RangeSafe installation video shows the knobs going onto a real range —
              the same written instructions shipped with every order, plus insert extensions where a
              stove requires them.
            </p>
            <ul className="mt-8 space-y-3 border-t border-line pt-6">
              {INSTALL_RESOURCES.videos.map((v) => (
                <li key={v.href}>
                  <a href={v.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-dim transition-colors hover:text-white" data-testid={`how-video-${v.href.split('/').pop()}`}>
                    <Play className="h-3 w-3 text-accentText" aria-hidden="true" /> {v.title} <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
            <a href={INSTALL_RESOURCES.guide.href} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-silver" data-testid="how-install-guide">
              <FileText className="h-3.5 w-3.5" aria-hidden="true" /> Installation Guide (PDF)
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-line bg-black">
              <iframe
                src="https://player.vimeo.com/video/442218789"
                title="How to Install RangeSafe Stove Safety Knobs (video)"
                allow="autoplay; fullscreen; picture-in-picture"
                loading="lazy"
                className="aspect-video w-full"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
              Hosted on Vimeo · official RangeSafe demonstration
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line" data-testid="how-compatibility">
        <div className="mx-auto max-w-shell px-5 py-20 sm:px-8 lg:py-28">
          <SectionHead eyebrow="Before You Buy" lines={['What the knobs need']} lead="RangeSafe knobs work with most stove models — not every stove. Three fit considerations decide most cases." />
          <div className="mt-12 grid gap-px border border-line bg-line lg:grid-cols-3">
            {COMPATIBILITY_LIMITS.map((lim, i) => (
              <Reveal key={lim.title} delay={i * 0.06} className="bg-base">
                <div className="h-full p-8">
                  <p className="font-mono text-[11px] tracking-[0.3em] text-accentText">0{i + 1}</p>
                  <h3 className="mt-3 font-display text-xl font-bold uppercase tracking-tight text-white">{lim.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-dim">{lim.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col gap-4 sm:flex-row">
              <Link to="/quote" className={BTN_PRIMARY} data-testid="how-quote-cta">
                Request a commercial quote
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
